import { authClient } from "@/api/auth";
import { BackgroundDots } from "@msuite/ui/background";
import { Button } from "@msuite/ui/button";
import { AuthForm, PasswordIndicator, TextInput } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { type UserSignUpSchema, userSignUpSchema } from "@msuite/utils/schemas";
import { getFullName } from "@msuite/utils/users";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

export function SignUp() {
	/** Hooks */
	const navigate = useNavigate();
	const { t } = useTranslation();

	/** Form */
	const methods = useForm<UserSignUpSchema>({
		mode: "onSubmit",
		resolver: resolver(userSignUpSchema),
	});
	const [password] = useWatch({ control: methods.control, name: ["password"] });

	/** Functions */
	async function handleOnSubmit({ email, password, firstName, lastName }: UserSignUpSchema) {
		try {
			await authClient.signUp.email(
				{
					email,
					password,
					name: getFullName({ firstName, lastName }),
					firstName,
					lastName,
					state: "active",
				},
				{
					onError: (context) => {
						toast.error(context.error.message);
					},
				},
			);
		} catch {
			toast.error("Error");
		}
	}

	/** Render */
	return (
		<div className="flex-center w-screen h-screen">
			<BackgroundDots />
			<AuthForm
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				data-cy="sign-up-form"
			>
				<AuthForm.Header />
				<AuthForm.Body>
					<AuthForm.Row>
						<TextInput
							label={t("profile.first_name")}
							placeholder="Max"
							control={methods.control}
							name="firstName"
							isRequired
							data-cy="sign-up-first-name-input"
						/>
						<TextInput
							label={t("profile.last_name")}
							placeholder="Mustermann"
							control={methods.control}
							name="lastName"
							isRequired
							data-cy="sign-up-last-name-input"
						/>
					</AuthForm.Row>
					<TextInput
						label={t("personnel.fields.email")}
						placeholder="max@mustermann.de"
						control={methods.control}
						name="email"
						isRequired
						data-cy="sign-up-email-input"
					/>
					<div className="v-stack">
						<TextInput
							type="password"
							label={t("auth.password")}
							isRequired
							control={methods.control}
							name="password"
							placeholder="Password"
							data-cy="sign-up-password-input"
						/>
						<PasswordIndicator currentPassword={password} />
					</div>
					<TextInput
						type="password"
						label={t("auth.confirm_password")}
						isRequired
						control={methods.control}
						name="confirmPassword"
						placeholder="Password"
						data-cy="sign-up-confirm-password-input"
					/>
				</AuthForm.Body>
				<AuthForm.Footer>
					<Button
						type="submit"
						variant="primary"
						size="sm"
						isLoading={methods.formState.isSubmitting}
					>
						{t("general.submit")}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate(-1)}
					>
						{t("general.cancel")}
					</Button>
				</AuthForm.Footer>
			</AuthForm>
		</div>
	);
}
